import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, Plus, Minus, AlertTriangle, Loader2, Ban, Bike, ShoppingBag, Utensils } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../lib/utils';
import { VoidConfirmModal } from './VoidConfirmModal';
import { logVoidAudit, updateVoidedColumns } from '../../lib/void-audit';
type DeliveryTypeKey = 'delivery' | 'pickup' | 'dine_in';
const DELIVERY_TYPE_OPTIONS: {
  key: DeliveryTypeKey;
  label: string;
  Icon: ComponentType<{
    className?: string;
  }>;
}[] = [{
  key: 'delivery',
  label: 'Express',
  Icon: Bike
}, {
  key: 'pickup',
  label: 'Para llevar',
  Icon: ShoppingBag
}, {
  key: 'dine_in',
  label: 'Comer en local',
  Icon: Utensils
}];
interface ModifyItem {
  id: number | string;
  item_name_snapshot: string;
  quantity: number;
  unit_price_snapshot: number;
  line_total: number;
  status: string;
  notes: string | null;
  // Opciones seleccionadas del ítem (ej. tipo de natural, de qué es el
  // casado). Sólo para mostrar el detalle al cajero — additivo, no
  // participa en ningún cálculo de dinero.
  selected_options?: Array<{
    option_group?: string | null;
    option_name?: string | null;
  }>;
}
interface CabanaModifyOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  /** Called after any successful modification or cancellation */
  onChanged: () => void;
  /** Called when the user cancels the ENTIRE order */
  onCancelOrder: (orderId: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  restaurantId?: number;
}
/**
 * Cabaña Parrillera — exclusive cashier tool to modify or cancel an
 * order that is already in progress (incl. while it is in the kitchen).
 *
 * Scope:
 *  - Change item quantities (incl. removing items entirely).
 *  - Cancel the full order with one click.
 *
 * Safety:
 *  - All edits run as targeted supabase updates and never touch
 *    unrelated columns. The orders.subtotal and orders.total are
 *    recomputed from the surviving (non-cancelled) items + the
 *    existing delivery_fee so payment screens stay accurate.
 */
export function CabanaModifyOrderModal({
  isOpen,
  onClose,
  orderId,
  onChanged,
  onCancelOrder,
  restaurantId
}: CabanaModifyOrderModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<ModifyItem[]>([]);
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [orderStatus, setOrderStatus] = useState<string>('');
  const [deliveryFee, setDeliveryFee] = useState<number>(0);
  const [deliveryType, setDeliveryType] = useState<DeliveryTypeKey | ''>('');
  const [savingDeliveryType, setSavingDeliveryType] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellingAll, setCancellingAll] = useState(false);
  const [confirmCancelAll, setConfirmCancelAll] = useState(false);
  // Void Auth State
  const [voidModalState, setVoidModalState] = useState<{
    isOpen: boolean;
    action: 'cancel_order' | 'remove_item';
    targetId?: string | number;
    targetName?: string;
    originalAmount?: number;
  }>({
    isOpen: false,
    action: 'cancel_order'
  });
  // Load order + items whenever the modal opens
  useEffect(() => {
    if (!isOpen || !orderId) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const {
          data: order,
          error: ordErr
        } = await supabase.from('orders').select('id, order_number, status, delivery_fee, delivery_type').eq('id', orderId).maybeSingle();
        if (ordErr) throw ordErr;
        if (cancelled) return;
        if (!order) {
          setError('No se encontró la orden');
          setItems([]);
          return;
        }
        setOrderNumber(order.order_number || '');
        setOrderStatus(order.status || '');
        setDeliveryFee(Number(order.delivery_fee) || 0);
        const dt = String(order.delivery_type || '').trim();
        setDeliveryType(dt === 'delivery' || dt === 'pickup' || dt === 'dine_in' ? dt as DeliveryTypeKey : '');
        const {
          data: rows,
          error: itemsErr
        } = await supabase.from('order_items').select('id, item_name_snapshot, quantity, unit_price_snapshot, line_total, status, notes').eq('order_id', orderId).order('id', {
          ascending: true
        });
        if (itemsErr) throw itemsErr;
        if (cancelled) return;
        // Rancho San Francisco (id 28) — enriquecemos cada ítem con sus
        // opciones (tipo de natural, de qué es el casado, etc.) para que
        // el cajero vea el detalle completo al decidir cuál eliminar.
        // No-bloqueante y SOLO para id 28: si falla, seguimos sin
        // opciones; otros restaurantes mantienen el flujo intacto.
        const optsByItem: Record<string, any[]> = {};
        if (Number(restaurantId) === 28) {
          try {
            const ids = (rows || []).map((r: any) => r.id);
            if (ids.length > 0) {
              const {
                data: optRows
              } = await supabase.from('order_item_options').select('order_item_id, option_group, option_name').in('order_item_id', ids);
              (optRows || []).forEach((o: any) => {
                if (!optsByItem[o.order_item_id]) optsByItem[o.order_item_id] = [];
                optsByItem[o.order_item_id].push({
                  option_group: o.option_group,
                  option_name: o.option_name
                });
              });
            }
          } catch {
            // opciones son sólo decorativas; ignoramos errores
          }
        }
        if (cancelled) return;
        setItems((rows || []).map(r => ({
          id: r.id,
          item_name_snapshot: r.item_name_snapshot,
          quantity: Number(r.quantity) || 0,
          unit_price_snapshot: Number(r.unit_price_snapshot) || 0,
          line_total: Number(r.line_total) || 0,
          status: r.status || 'new',
          notes: r.notes,
          selected_options: optsByItem[r.id] || []
        })));
      } catch (e: any) {
        if (!cancelled) {
          console.error('[CabanaModifyOrderModal] load error:', e);
          setError(e?.message || 'Error al cargar la orden');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    // Reset confirm-all gate each time we open
    setConfirmCancelAll(false);
    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId]);
  const activeItems = useMemo(() => items.filter(it => it.status !== 'cancelled'), [items]);
  const cancelledItems = useMemo(() => items.filter(it => it.status === 'cancelled'), [items]);
  const subtotal = useMemo(() => activeItems.reduce((sum, it) => sum + (Number(it.line_total) || 0), 0), [activeItems]);
  const total = subtotal + deliveryFee;
  // Recompute the order's subtotal/total from the surviving items
  const recomputeOrderTotals = async (orderId: string, nextItems: ModifyItem[]) => {
    const newSubtotal = nextItems.filter(it => it.status !== 'cancelled').reduce((sum, it) => sum + (Number(it.line_total) || 0), 0);
    const newTotal = newSubtotal + deliveryFee;
    const {
      error: updErr
    } = await supabase.from('orders').update({
      subtotal: newSubtotal,
      total: newTotal
    }).eq('id', orderId);
    if (updErr) throw updErr;
  };
  const handleChangeDeliveryType = async (next: DeliveryTypeKey) => {
    if (!orderId) return;
    if (next === deliveryType) return;
    if (savingDeliveryType) return;
    setSavingDeliveryType(true);
    setError(null);
    const prev = deliveryType;
    // Optimistic UI update
    setDeliveryType(next);
    try {
      // Only mutate `delivery_type`. We do NOT touch delivery_fee,
      // table_id, address, etc. so existing values are preserved — the
      // cashier can adjust those separately from the regular order
      // detail view if needed.
      const {
        error: updErr
      } = await supabase.from('orders').update({
        delivery_type: next
      }).eq('id', orderId);
      if (updErr) throw updErr;
      onChanged();
    } catch (e: any) {
      console.error('[CabanaModifyOrderModal] delivery type change error:', e);
      setError(e?.message || 'No se pudo cambiar el tipo de entrega');
      // Revert on failure
      setDeliveryType(prev);
    } finally {
      setSavingDeliveryType(false);
    }
  };
  const handleQtyChange = async (itemId: number | string, delta: number) => {
    if (!orderId || saving) return;
    const target = items.find(it => it.id === itemId);
    if (!target) return;
    const newQty = target.quantity + delta;
    if (newQty < 0) return;
    if (newQty === 0) {
      setVoidModalState({
        isOpen: true,
        action: 'remove_item',
        targetId: itemId,
        targetName: target.item_name_snapshot,
        originalAmount: target.line_total
      });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const newLineTotal = newQty * target.unit_price_snapshot;
      const {
        error: itErr
      } = await supabase.from('order_items').update({
        quantity: newQty,
        line_total: newLineTotal
      }).eq('id', itemId);
      if (itErr) throw itErr;
      const next = items.map(it => it.id === itemId ? {
        ...it,
        quantity: newQty,
        line_total: newLineTotal
      } : it);
      setItems(next);
      await recomputeOrderTotals(orderId, next);
      onChanged();
    } catch (e: any) {
      console.error('[CabanaModifyOrderModal] qty change error:', e);
      setError(e?.message || 'No se pudo actualizar la cantidad');
    } finally {
      setSaving(false);
    }
  };
  const handleRemoveItem = async (itemId: number | string) => {
    if (!orderId || saving) return;
    const target = items.find(it => it.id === itemId);
    if (!target) return;
    setVoidModalState({
      isOpen: true,
      action: 'remove_item',
      targetId: itemId,
      targetName: target.item_name_snapshot,
      originalAmount: target.line_total
    });
  };
  const handleCancelEntireOrder = async () => {
    if (!orderId) return;
    setVoidModalState({
      isOpen: true,
      action: 'cancel_order',
      targetId: orderId,
      originalAmount: total
    });
  };
  const handleVoidConfirm = async (authorizedBy: string, reason: string) => {
    if (!orderId || !restaurantId) return;
    setVoidModalState(prev => ({
      ...prev,
      isOpen: false
    }));
    if (voidModalState.action === 'remove_item' && voidModalState.targetId) {
      setSaving(true);
      setError(null);
      try {
        // 1) Acción ESENCIAL: cancelar la línea del pedido. Intentamos
        //    primero sellar las columnas de auditoría (voided_at/by,
        //    void_reason + status='cancelled'). Si esas columnas no
        //    existen en este restaurante, NO bloqueamos: hacemos el
        //    cancel "plano" para que el cajero igual pueda avanzar.
        try {
          await updateVoidedColumns('order_items', Number(voidModalState.targetId), authorizedBy, reason);
        } catch (voidColErr: any) {
          console.warn('[CabanaModifyOrderModal] columnas de auditoría no disponibles, usando cancelación simple:', voidColErr);
          const {
            error: itErr
          } = await supabase.from('order_items').update({
            status: 'cancelled'
          }).eq('id', voidModalState.targetId);
          if (itErr) throw itErr;
        }
        // 2) Registro de auditoría — best-effort. Si la tabla
        //    void_audit_log no existe, NO bloqueamos la eliminación.
        try {
          await logVoidAudit({
            restaurant_id: restaurantId,
            order_id: Number(orderId),
            order_item_id: Number(voidModalState.targetId),
            action_type: 'remove_item',
            authorized_by: authorizedBy,
            reason,
            original_amount: voidModalState.originalAmount || 0,
            item_name: voidModalState.targetName
          });
        } catch (logErr: any) {
          console.warn('[CabanaModifyOrderModal] no se pudo registrar la auditoría (no bloqueante):', logErr);
        }
        // 3) Estado local + recálculo + notificar.
        const next = items.map(it => it.id === voidModalState.targetId ? {
          ...it,
          status: 'cancelled'
        } : it);
        setItems(next);
        await recomputeOrderTotals(orderId, next);
        onChanged();
      } catch (e: any) {
        console.error('[CabanaModifyOrderModal] void remove error:', e);
        setError(e?.message || 'No se pudo quitar el artículo');
      } finally {
        setSaving(false);
      }
    } else if (voidModalState.action === 'cancel_order') {
      setCancellingAll(true);
      setError(null);
      try {
        // Sellado de auditoría — best-effort, no bloquea la cancelación.
        try {
          await updateVoidedColumns('orders', Number(orderId), authorizedBy, reason);
        } catch (voidColErr: any) {
          console.warn('[CabanaModifyOrderModal] columnas de auditoría de orden no disponibles (no bloqueante):', voidColErr);
        }
        try {
          await logVoidAudit({
            restaurant_id: restaurantId,
            order_id: Number(orderId),
            action_type: 'cancel_order',
            authorized_by: authorizedBy,
            reason,
            original_amount: voidModalState.originalAmount || 0
          });
        } catch (logErr: any) {
          console.warn('[CabanaModifyOrderModal] no se pudo registrar la auditoría de cancelación (no bloqueante):', logErr);
        }
        // Acción ESENCIAL: cancelar la orden. onCancelOrder se encarga
        // del resto del cleanup (tickets de anulación, etc.).
        const res = await onCancelOrder(orderId);
        if (!res.success) {
          setError(res.error || 'No se pudo cancelar la orden');
          return;
        }
        onChanged();
        onClose();
      } catch (e: any) {
        console.error('[CabanaModifyOrderModal] void cancel error:', e);
        setError(e?.message || 'No se pudo cancelar la orden');
      } finally {
        setCancellingAll(false);
      }
    }
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-496" data-mp="3g0q4c32jardz">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-497" data-mp="3kxgfcbzhewsj">
        <motion.div initial={{
        y: 40,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 40,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 28
      }} className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-498" data-mp="2x95o80jl8bhg">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-499" data-mp="3ltc5qm49147g">
            <div data-id="element-500" data-mp="3gvu3fekqln8f">
              <h2 className="text-lg font-black text-gray-900 leading-tight" data-id="element-501" data-mp="3gu98hnuig2a1">
                Modificar / Cancelar
              </h2>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-502" data-mp="3uoa2y5efj34l">
                Orden #{orderNumber || orderId} · {orderStatus}
              </p>
            </div>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors" aria-label="Cerrar" data-id="element-503" data-mp="32w2kczfd5gnk">
              <X className="w-5 h-5" data-id="element-504" data-mp="36wuwq1jj81o2" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-505" data-mp="r5qj9r9th5le">
            {loading ? <div className="flex items-center justify-center py-16 text-gray-400" data-id="element-506" data-mp="r7jr39w61vuz">
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-507" data-mp="m8qcfe557yt8" />
              </div> : error ? <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2" data-id="element-508" data-mp="upr0gk2xleh0">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-509" data-mp="kpqd8n1xye7j" />
                <span data-id="element-510" data-mp="krl1aa8iemfu">{error}</span>
              </div> : null}

            {/* Delivery type selector — La Cabaña can re-assign the
             fulfilment channel of an in-progress order (e.g. switch a
             delivery to pickup or to dine-in) without recreating it. */}
            {!loading && !error && <div className="mb-5" data-id="element-511" data-mp="13om3jnaoi8g3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2" data-id="element-512" data-mp="13njhbsoawu6i">
                  Tipo de entrega
                </p>
                <div className="grid grid-cols-3 gap-2" data-id="element-513" data-mp="1wd04obx2dkvv">
                  {DELIVERY_TYPE_OPTIONS.map(({
                key,
                label,
                Icon
              }) => {
                const isActive = deliveryType === key;
                return <button key={key} type="button" onClick={() => handleChangeDeliveryType(key)} disabled={savingDeliveryType} className={'relative flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-xl border-2 text-xs font-semibold transition-all active:scale-95 disabled:opacity-50 ' + (isActive ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]' : 'border-gray-200 bg-white text-gray-600 hover:border-[#FF6B35]/40 hover:text-gray-900')} data-id="element-514" data-mp="120kbovoglot">
                        <Icon className="w-5 h-5" data-id="element-515" data-mp="601z3xm2lyuh" />
                        <span className="leading-tight text-center" data-id="element-516" data-mp="2vpm1q5b95u40">
                          {label}
                        </span>
                        {isActive && savingDeliveryType && <Loader2 className="absolute top-1.5 right-1.5 w-3 h-3 animate-spin text-[#FF6B35]" data-id="element-517" data-mp="1690defaeqhqf" />}
                      </button>;
              })}
                </div>
                <p className="text-[11px] text-gray-400 mt-2 leading-relaxed" data-id="element-518" data-mp="1f8dmv7yj76b5">
                  Cambia el canal del pedido sin recrear la orden. Mesa,
                  dirección y monto de express se conservan tal cual.
                </p>
              </div>}

            {!loading && activeItems.length === 0 && !error && <div className="py-10 text-center text-gray-500 text-sm" data-id="element-519" data-mp="1u0fg4963j127">
                Esta orden ya no tiene artículos activos.
              </div>}

            {/* Active items */}
            <div className="space-y-3" data-id="element-520" data-mp="22wwtzk5uhd1n">
              {activeItems.map(item => <div key={String(item.id)} className="border border-gray-200 rounded-xl p-3 flex gap-3" data-id="element-521" data-mp="4l7oiyz8p8tu">
                  <div className="flex-1 min-w-0" data-id="element-522" data-mp="3okfaarf8zwyu">
                    <p className="font-bold text-gray-900 truncate" data-id="element-523" data-mp="3ohkrt5fsom82">
                      {item.item_name_snapshot}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-524" data-mp="2bxn8ahs8uic">
                      {formatCurrency(item.unit_price_snapshot)} c/u
                    </p>
                    {Number(restaurantId) === 28 && Array.isArray(item.selected_options) && item.selected_options.length > 0 && <div className="mt-1.5 space-y-0.5" data-id="element-525" data-mp="g29ey8aggr8y">
                          {item.selected_options.map((opt, idx) => <p key={idx} className="text-xs text-gray-700 font-medium" data-id="element-526" data-mp="l1437pr6z80g" data-mpi={idx}>
                              · {opt.option_name}
                            </p>)}
                        </div>}
                    {item.notes && <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2" data-id="element-527" data-mp="2kb6tj72bv5ss">
                        Nota: {item.notes}
                      </p>}
                  </div>

                  <div className="flex flex-col items-end justify-between shrink-0" data-id="element-528" data-mp="2ahyek0hvj62y">
                    <span className="font-bold text-gray-900" data-id="element-529" data-mp="25msr2x8tpjcr">
                      {formatCurrency(item.line_total)}
                    </span>
                    <div className="flex items-center gap-1 mt-2" data-id="element-530" data-mp="2y93s290cdma3">
                      <button onClick={() => handleQtyChange(item.id, -1)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 active:scale-95 transition-all" aria-label="Disminuir" data-id="element-531" data-mp="2y7soa96wceiz">
                        <Minus className="w-4 h-4" data-id="element-532" data-mp="1cyeumlxduv3b" />
                      </button>
                      <span className="w-8 text-center font-bold text-gray-900 tabular-nums" data-id="element-533" data-mp="4xmtuicjhbaa">
                        {item.quantity}
                      </span>
                      <button onClick={() => handleQtyChange(item.id, +1)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 active:scale-95 transition-all" aria-label="Aumentar" data-id="element-534" data-mp="3r4usl1syejxc">
                        <Plus className="w-4 h-4" data-id="element-535" data-mp="38ed459f5mahs" />
                      </button>
                      <button onClick={() => handleRemoveItem(item.id)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-40 active:scale-95 transition-all ml-1" aria-label="Quitar artículo" data-id="element-536" data-mp="3dcexqoa7hs7i">
                        <Trash2 className="w-4 h-4" data-id="element-537" data-mp="2fr3oykaaahm4" />
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Cancelled items (informational) */}
            {cancelledItems.length > 0 && <div className="mt-5" data-id="element-538" data-mp="2ou4twhney2o7">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2" data-id="element-539" data-mp="32r1l4k7g1m5m">
                  Quitados de la orden
                </p>
                <div className="space-y-1.5" data-id="element-540" data-mp="2ynvg1fw2i10q">
                  {cancelledItems.map(item => <div key={String(item.id)} className="flex items-center justify-between text-sm text-gray-400 line-through px-3 py-1.5 bg-gray-50 rounded-lg" data-id="element-541" data-mp="3cjgzl7jj7slr">
                      <span className="truncate pr-2" data-id="element-542" data-mp="3hgpjpdo8q6pn">
                        {item.quantity}x {item.item_name_snapshot}
                      </span>
                      <span className="shrink-0" data-id="element-543" data-mp="1et938pwc0lry">
                        {formatCurrency(item.line_total)}
                      </span>
                    </div>)}
                </div>
              </div>}
          </div>

          {/* Totals + actions */}
          <div className="border-t border-gray-100 px-5 py-4 bg-gray-50 shrink-0" data-id="element-544" data-mp="1thddr6l8dqzn">
            <div className="space-y-1 mb-4" data-id="element-545" data-mp="vvpuzohp776l">
              <div className="flex justify-between text-sm text-gray-600" data-id="element-546" data-mp="vx0x7jvhw6vl">
                <span data-id="element-547" data-mp="10uj7pep5uu5p">Subtotal</span>
                <span className="font-medium tabular-nums" data-id="element-548" data-mp="10vu7z4jedjs2">
                  {formatCurrency(subtotal)}
                </span>
              </div>
              {deliveryFee > 0 && <div className="flex justify-between text-sm text-gray-600" data-id="element-549" data-mp="1aq44myfdh48l">
                  <span data-id="element-550" data-mp="2h7r7r2ndlzwv">Express</span>
                  <span className="font-medium tabular-nums" data-id="element-551" data-mp="2m7c89hi72gbp">
                    {formatCurrency(deliveryFee)}
                  </span>
                </div>}
              <div className="flex justify-between text-lg font-black text-gray-900 pt-1 border-t border-gray-200 mt-1" data-id="element-552" data-mp="aj7anq5lieww">
                <span data-id="element-553" data-mp="5n9yzx5a1wqg">Total</span>
                <span className="tabular-nums" data-id="element-554" data-mp="tfe4qdxrbps">{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Cancel-entire-order — two-step confirmation */}
            {!confirmCancelAll ? <button onClick={() => setConfirmCancelAll(true)} disabled={saving || cancellingAll} className="w-full h-12 rounded-xl font-bold text-sm bg-white border-2 border-red-200 text-red-600 hover:bg-red-50 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-555" data-mp="3n26x1bj4nfjw">
                <Ban className="w-4 h-4" data-id="element-556" data-mp="1c9vzt1g32ol3" />
                Cancelar pedido completo
              </button> : <div className="space-y-2" data-id="element-557" data-mp="17bb0d48no8xp">
                <p className="text-sm text-red-700 text-center font-medium" data-id="element-558" data-mp="1786pxp4lkgyl">
                  ¿Seguro que quieres cancelar TODO el pedido?
                </p>
                <div className="grid grid-cols-2 gap-2" data-id="element-559" data-mp="36hztdrpzinpg">
                  <button onClick={() => setConfirmCancelAll(false)} disabled={cancellingAll} className="h-12 rounded-xl font-bold text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50" data-id="element-560" data-mp="2rsbokgilh6wz">
                    No, mantener
                  </button>
                  <button onClick={handleCancelEntireOrder} disabled={cancellingAll} className="h-12 rounded-xl font-bold text-sm bg-red-600 hover:bg-red-700 text-white active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-561" data-mp="3aitfqgns3ug8">
                    {cancellingAll ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-562" data-mp="240mtisee24cc" /> : <Ban className="w-4 h-4" data-id="element-563" data-mp="m77ar0784l4n" />}
                    Sí, cancelar todo
                  </button>
                </div>
              </div>}
          </div>
        </motion.div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-[#141414] flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-564" data-mp="r3o0tzxzh9kd">
          <button onClick={() => setConfirmCancelAll(true)} disabled={saving || cancellingAll} className="w-full sm:w-auto px-4 py-2 rounded-xl text-sm font-bold text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50" data-id="element-565" data-mp="3fee8vsy4hs6">
            Cancelar Orden Completa
          </button>
          <button onClick={onClose} className="w-full sm:w-auto px-6 py-2 rounded-xl text-sm font-bold bg-white text-black hover:bg-gray-200 transition-colors" data-id="element-566" data-mp="7edi0d2xy8i1">
            Cerrar
          </button>
        </div>

        {/* ⚠️ FIX: el VoidConfirmModal vive DENTRO del fondo (motion.div
            con onClick={onClose}) pero FUERA del contenedor de contenido
            que hace stopPropagation. Sin este wrapper, al hacer click en
            los inputs ("Nombre de quien autoriza" / "Motivo") el evento
            burbujeaba hasta el fondo y cerraba TODO el modal — sacaba al
            usuario sin dejarlo escribir. Frenamos la propagación acá para
            que los clicks del modal de anulación nunca cierren el modal
            padre. Cero impacto visual: VoidConfirmModal es un overlay
            fixed propio. */}
        <div onClick={e => e.stopPropagation()} data-id="element-567" data-mp="x90jokfambq5">
          <VoidConfirmModal isOpen={voidModalState.isOpen} onClose={() => setVoidModalState(prev => ({
          ...prev,
          isOpen: false
        }))} onConfirm={handleVoidConfirm} title={voidModalState.action === 'cancel_order' ? 'Anular Orden Completa' : 'Eliminar Artículo'} description={voidModalState.action === 'cancel_order' ? 'Por favor, indica quién autoriza y el motivo de la anulación de la orden. Esta acción quedará registrada en el reporte de caja.' : 'Por favor, indica quién autoriza y el motivo de la eliminación de este artículo. Esta acción quedará registrada en el reporte de caja.'} data-id="element-568" data-mp="119u5n896dgge" />
        </div>
      </motion.div>
    </AnimatePresence>;
}